import { FC, useState } from 'react';
import { Box, styled, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import { icons, wallets } from 'config/constants/assets';
import { ContainedButton } from 'components/common/Button';
import { addLpTokenToMetamask } from 'utils/wallet';
import { LiquidityInfo } from 'types/liquidity';
import { getTokens } from 'utils/tokens';
import { DEFAULT_ACTIVE_CHAIN_ID } from 'config/constants/chains';

const Container = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: '20px',
  padding: '0px 32px',
  background: theme.palette.background.card,
  boxShadow:
    '0px 100px 77px rgba(0, 0, 0, 0.13), 0px 25.9935px 52.3711px rgba(0, 0, 0, 0.0989853), 0px 10.1608px 47.2956px rgba(0, 0, 0, 0.0902431), 0px 5.70356px 44.4605px rgba(0, 0, 0, 0.0829201), 0px 3.80638px 39.8596px rgba(0, 0, 0, 0.0717558), 0px 2.15748px 29.2214px rgba(0, 0, 0, 0.0505145)',

  [theme.breakpoints.up('xs')]: {
    padding: '0px 16px',
  },
  [theme.breakpoints.up('md')]: {
    padding: '0px 32px',
  },
}));

// toggle section
const ToggleSection = styled(Box)(() => ({
  position: 'absolute',
  right: '-14px',
  top: 'calc(50% - 14px)',
  background: '#2A3139',
  boxShadow: '0px 3px 4px rgba(0, 0, 0, 0.25)',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  width: '28px',
  height: '28px',
}));

const ToggleIcon = styled('img')(() => ({}));

// origin section
const Origin = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-around',
  // flexWrap: 'wrap',
  gap: '15px',
  padding: '16px 0px',

  [theme.breakpoints.up('xs')]: {
    marginTop: '-15px',
    flexWrap: 'wrap',
  },
  [theme.breakpoints.up('sm')]: {
    marginTop: '0px',
    flexWrap: 'nowrap',
  },
}));

// token img section
const TokenImgSection = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
}));

const TokenImgWrapper = styled('img')(({ theme }) => ({
  position: 'absolute',
  width: '60px',

  [theme.breakpoints.up('xs')]: {
    width: '50px',
    top: '-3px',
  },
  [theme.breakpoints.up('md')]: {
    width: '60px',
    top: '-4px',
  },
}));

const TokenImg = styled('img')(({ theme }) => ({
  width: '60px',

  [theme.breakpoints.up('xs')]: {
    width: '50px',
    position: 'absolute',
    left: '-35px',
    top: '30px',
  },
  [theme.breakpoints.up('sm')]: {
    top: '12px',
  },
  [theme.breakpoints.up('md')]: {
    width: '55px',
    position: 'relative',
    left: '0px',
    top: '0px',
  },
}));

const TokenName = styled(Typography)(({ theme }) => ({
  fontFamily: 'Prompt',
  fontStyle: 'normal',
  fontWeight: '700',
  cursor: 'pointer',
  fontSize: '19px',
  lineHeight: '29px',

//   [theme.breakpoints.up('xs')]: {
//     //display: 'none',
//   },
  [theme.breakpoints.up('md')]: {
    display: 'block',
  },
}));

// info section
const InfoSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '30px',
  position: 'relative',
  textAlign: 'center',
  alignItems: 'baseline',

  [theme.breakpoints.up('xs')]: {
    gap: '15px',
    width: '100%',
    justifyContent: 'center',
  },
  [theme.breakpoints.up('sm')]: {
    width: 'auto',
  },
  [theme.breakpoints.up('md')]: {
    gap: '30px',
  },
}));

const InfoCell = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontFamily: 'Prompt',
  fontStyle: 'normal',
  fontWeight: '400',
  cursor: 'pointer',
  fontSize: '14px',
  lineHeight: '21px',
  color: 'rgba(255, 255, 255, 0.7)',

  img: {
    width: '20px',
    marginRight: '8px',
  },

  [theme.breakpoints.up('xs')]: {
    fontSize: '12px',
    lineHeight: '18px',
    img: {
      display: 'none',
    },
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '14px',
    lineHeight: '21px',
    img: {
      display: 'block',
    },
  },
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter',
  fontStyle: 'normal',
  fontWeight: '700',
  cursor: 'pointer',
  fontSize: '25px',
  lineHeight: '30px',
  color: '#ffffff',
  textAlign: 'start',
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'end',

  span: {
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: '400',
    marginLeft: '4px',
  },

  [theme.breakpoints.up('xs')]: {
    fontSize: '19px',
    lineHeight: '23px',
    span: {
      fontSize: '10px',
      lineHeight: '14px',
    },
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '20px',
    lineHeight: '25px',
    span: {
      fontSize: '12px',
      lineHeight: '16px',
    },
  },
}));

const InfoValueDollar = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter',
  fontStyle: 'normal',
  fontWeight: '700',
  cursor: 'pointer',
  fontSize: '25px',
  lineHeight: '30px',
  color: '#ffffff',
  textAlign: 'start',
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'end',

  span: {
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: '400',
    marginLeft: '4px',
  },

  [theme.breakpoints.up('xs')]: {
    fontSize: '19px',
    lineHeight: '23px',
    span: {
      fontSize: '10px',
      lineHeight: '14px',
    },
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '15px',
    lineHeight: '20px',
    span: {
      fontSize: '12px',
      lineHeight: '16px',
    },
  },
}));

// action section
const ActionSection = styled(Box)(() => ({
  position: 'relative',
  textAlign: 'center',
  alignItems: 'center',
  display: 'flex',
  gap: '10px',
}));

const AddButton = styled(ContainedButton)(({ theme }) => ({
  background: theme.palette.background.buttonGray,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '80px',
  height: '37px',

  span: {
    fontFamily: 'Prompt',
    fontStyle: 'normal',
    fontWeight: '400',
    cursor: 'pointer',
    fontSize: '24px',
    lineHeight: '18px',
    color: '#F89D35',
  },
}));

const WalletImg = styled('img')(() => ({
  width: '22px',
}));

// expanded section
const Expanded = styled(Box)(({ theme }) => ({
  borderTop: '1px solid rgba(255, 255, 255, 0.3)',
  display: 'flex',
  justifyContent: 'space-around',
  flexWrap: 'wrap',
  gap: '18px',
  alignItems: 'center',
  padding: '16px 0px',

  [theme.breakpoints.up('xs')]: {
    borderTop: '1px solid #CCC292',
  },
  [theme.breakpoints.up('sm')]: {
    borderTop: '1px solid rgba(255, 255, 255, 0.3)',
  },
}));

// global info section
const GlobalInfoSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '15px',
  position: 'relative',
  textAlign: 'center',
  alignItems: 'center',

  [theme.breakpoints.up('xs')]: {
    gap: '5px',
  },
  [theme.breakpoints.up('md')]: {
    gap: '15px',
  },
}));

const GlobalInfoCard = styled(Box)(({ theme }) => ({
  background: 'rgba(217, 217, 217, 0.2)',
  borderRadius: '10px',
  padding: '15px 15px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',

  [theme.breakpoints.up('xs')]: {
    padding: '10px 10px',
  },
  [theme.breakpoints.up('md')]: {
    padding: '15px 15px',
  },
}));

const GlobalInfoCardLabel = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontFamily: 'Prompt',
  fontStyle: 'normal',
  fontWeight: '400',
  cursor: 'pointer',

  color: 'rgba(255, 255, 255, 0.7)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',

  img: {
    width: '14px',
    marginRight: '10px',
  },

  [theme.breakpoints.up('xs')]: {
    fontSize: '9px',
    lineHeight: '14px',
    img: {
      display: 'none',
    },
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '14px',
    lineHeight: '21px',
    img: {
      display: 'block',
    },
  },
}));

const GlobalInfoCardValue = styled(Box)(({ theme }) => ({
  display: 'flex',
  fontFamily: 'Prompt',
  fontStyle: 'normal',
  fontWeight: '600',
  cursor: 'pointer',

  color: '#ffffff',

  '& > div': {
    width: '14px',
    marginRight: '10px',
  },

  [theme.breakpoints.up('xs')]: {
    fontSize: '14px',
    lineHeight: '21px',
    '& > div': {
      display: 'none',
    },
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '19px',
    lineHeight: '29px',
    '& > div': {
      display: 'block',
    },
  },
}));

const Divider = styled(Typography)(() => ({
  background: 'rgba(255, 255, 255, 0.7)',
  width: '1.5px',
  height: '100%',
  margin: 'auto',
}));

interface LiquidityCardProps {
  lp: LiquidityInfo;
}

const LiquidityCard: FC<LiquidityCardProps> = ({ lp }) => {
  const [isExpanded, setExpanded] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: tokenList } = useAppSelector((state) => state.tokens);
  const tokens = getTokens(DEFAULT_ACTIVE_CHAIN_ID);

  const { token0Address, totalSupply, tokenInLP, tokenAmount0, tokenAmount1, token0Symbol, token1Symbol, token1Address, address, isShort, addUrl }  = lp;

  const onHandleBuy = () => {
    if( addUrl ){
      window.open(addUrl)
      return;
    }
    window.open(`https://next-gen.basedfinance.io/dex/add/${token0Address}/${token1Address}`);
  };

  const onHandleAdd = async () => {
     await addLpTokenToMetamask(address, lp.tokenImage, lp.name);
  };

  const onHandleStake = () => {
    navigate('/earn');
  };

  const onHandleDex = () => {
     window.open(`https://dexscreener.com/fantom/${address}`);
  };

  const onToggle = () => {
    setExpanded(!isExpanded);
  };

  const getTokenPrice = (tokenAddr: string) => {
    const tokenInfo = tokenList.find((row) => row.address.toLowerCase() === tokenAddr.toLowerCase());
    if (tokenInfo) return tokenInfo.priceInUsd || 0;
    return 0;
  };

  const getTokenPriceFtm = (tokenAddr: string) => {
    const tokenInfo = tokenList.find((row) => row.address.toLowerCase() === tokenAddr.toLowerCase());
    if (tokenInfo) return tokenInfo.priceInFtm || 0;
    return 0;
  };

  const lpTokenPrice = tokenInLP * (getTokenPrice(token0Address) * 2)
  const lpTokenPriceFTM = tokenInLP * (getTokenPriceFtm(token0Address) * 2)

  return (
    <Container>
      {isShort &&
        <>
          <Origin>
          <TokenImgSection>
            <TokenImg alt="lp" src={lp.tokenImage} />
            <TokenName>{lp.symbol}</TokenName>
          </TokenImgSection>
          <InfoSection>
            <InfoCell>
              <InfoLabel>
                <span>
                  <img alt="circulating" src={icons.dollar} />
                </span>
                Liquidity
              </InfoLabel>
              <InfoValue>
              {`$${Number((totalSupply * lpTokenPrice).toFixed(0)).toLocaleString()} `}
              </InfoValue>
            </InfoCell>
            <InfoCell>
              <InfoLabel>
                <span>
                  <img alt="coins" src={icons.dollar} />
                </span>
                Total Supply
              </InfoLabel>
              {/* <InfoValue>
                {lpTokenPriceFTM.toFixed(2)}
                <span>FTM</span>
              </InfoValue> */}
              <InfoValue>
              {`${Number(totalSupply.toFixed(0)).toLocaleString()} `}
              </InfoValue>
            </InfoCell>
          </InfoSection>
          <ActionSection>
            <ContainedButton onClick={onHandleBuy}>Add</ContainedButton>
            <AddButton onClick={onHandleAdd} sx={{ minWidth: '80px' }}>
              <span>+</span>
              <WalletImg alt="logo" src={wallets.metamask} />
            </AddButton>
          </ActionSection>
        </Origin>
        </>
      }
      {!isShort &&
        <>
      <ToggleSection onClick={onToggle}>
        <ToggleIcon
          alt="toggle"
          src={icons.chevronDown}
          sx={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </ToggleSection>
      <Origin>
        <TokenImgSection>
          <TokenImg alt="lp" src={lp.tokenImage} />
          <TokenName>{lp.symbol}</TokenName>
        </TokenImgSection>
        <InfoSection>
          <InfoCell>
            <InfoLabel>
              <span>
                <img alt="price" src={icons.dollar} />
              </span>
              Lp price
            </InfoLabel>
            <InfoValue>
              {tokenAmount0.toFixed(2)}
              <span>{token0Symbol}</span>
              <span>&nbsp;/</span>
              &nbsp;{tokenAmount1.toFixed(2)}
              <span>{token1Symbol[0] === 'WFTM' ? 'FTM' : token1Symbol}</span>
            </InfoValue>
          </InfoCell>
          <InfoCell>
            <InfoLabel>
              <span>
                <img alt="price" src={icons.dollar} />
              </span>
              Current Price
            </InfoLabel>
            {/* <InfoValue>
              {lpTokenPriceFTM.toFixed(2)}
              <span>FTM</span>
            </InfoValue> */}
            <InfoValue>
            {lpTokenPrice.toFixed(2)}
              <span>USD</span>
            </InfoValue>
          </InfoCell>
        </InfoSection>
        <ActionSection>
          <ContainedButton onClick={onHandleBuy}>Buy</ContainedButton>
          <AddButton onClick={onHandleAdd} sx={{ minWidth: '80px' }}>
            <span>+</span>
            <WalletImg alt="logo" src={wallets.metamask} />
          </AddButton>
        </ActionSection>
      </Origin>
        </>
      }

      {isExpanded && (
        <Expanded>
          <GlobalInfoSection>
            {/* circulating supply */}
            <GlobalInfoCard>
              <GlobalInfoCardLabel>
                <img alt="circulating" src={icons.circulating} />
                Liquidity
              </GlobalInfoCardLabel>
              <GlobalInfoCardValue>
                <Box>
                  <Divider />
                </Box>
                {`$${Number((totalSupply * lpTokenPrice).toFixed(0)).toLocaleString()} `}
              </GlobalInfoCardValue>
            </GlobalInfoCard>

            {/* circulating supply */}
            <GlobalInfoCard>
              <GlobalInfoCardLabel>
                <img alt="coins" src={icons.coins} />
                Total Supply
              </GlobalInfoCardLabel>
              <GlobalInfoCardValue>
                <Box>
                  <Divider />
                </Box>
                {`${Number(totalSupply.toFixed(0)).toLocaleString()} `}
              </GlobalInfoCardValue>
            </GlobalInfoCard>
          </GlobalInfoSection>
          <ActionSection>
            <ContainedButton onClick={onHandleStake}>Earn</ContainedButton>
            <ContainedButton onClick={onHandleDex} sx={{ minWidth: '70px', maxHeight: '37px' }}>
                <WalletImg alt="logo" src={wallets.dexscreener} />
            </ContainedButton>
          </ActionSection>
        </Expanded>
      )}
    </Container>
  );
};

export default LiquidityCard;
